import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from 'next-themes';
import Sidebar from './Sidebar';
import OfflineIndicator from '@/components/OfflineIndicator';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Moon, Sun, LogOut, User, Menu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api from '@/lib/api';

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { t, toggleLanguage, isGujarati } = useLanguage();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [companyName, setCompanyName] = useState('K. G. Patel & Company');

  useEffect(() => {
    api.get('/settings').then((r) => setCompanyName(r.data.company_name)).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden" data-testid="dashboard-layout">
      <Sidebar open={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b bg-card flex items-center px-4 gap-3 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-8 w-8"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            <Menu className="h-4 w-4" />
          </Button>

          <h1
            className="text-base font-semibold truncate hidden sm:block"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="company-name"
          >
            {companyName}
          </h1>

          <div className="flex-1" />

          <OfflineIndicator />

          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-muted/50">
            <span className="text-[11px] font-medium text-muted-foreground">EN</span>
            <Switch
              checked={isGujarati}
              onCheckedChange={toggleLanguage}
              className="h-4 w-7 data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-300"
              data-testid="language-toggle"
            />
            <span className="text-[11px] font-medium text-muted-foreground">{"\u0A97\u0AC1"}</span>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            data-testid="dark-mode-toggle"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="user-menu">
                <User className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem disabled className="text-xs font-medium">
                {user?.name || user?.email}
              </DropdownMenuItem>
              <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                {t('role')}: {user?.role}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-3.5 w-3.5 mr-2" />
                {t('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 min-h-0 overflow-y-auto p-5 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
